import React from "react";
import cn from "classnames";
import styles from "./half-split-container.module.scss";
import { useDraggable } from "@src/hooks/use-draggable";

interface HalfSplitContainerPropTypes {
  children: React.ReactNode;
  alignCenter?: boolean;
  flexGrow?: boolean;
  isDraggable?: boolean; // Prop to enable or disable dragging
}

const cssClassName = "half-split-container";

export const HalfSplitContainer = ({
  children,
  alignCenter = false,
  flexGrow = false,
  isDraggable = false,
}: HalfSplitContainerPropTypes) => {
  const {
    isDragging,
    leftWidth,
    rightWidth,
    containerRef,
    leftContainerRef,
    rightContainerRef,
    onMouseDown,
    handleKeyDown,
  } = useDraggable(isDraggable);
  const childArray = React.Children.toArray(children);
  const midpoint = Math.ceil(childArray.length / 2);

  const firstHalfChildren = childArray.slice(0, midpoint);
  const secondHalfChildren = childArray.slice(midpoint);

  const alignCenterOrNot = () => {
    return alignCenter ? styles[`${cssClassName}--align-center`] : "";
  };
  const growOrNot = (block = "") => {
    return flexGrow ? styles[`${cssClassName}${block}--flex-grow`] : "";
  };

  return (
    <div
      ref={containerRef}
      className={`${styles[cssClassName]} ${alignCenterOrNot()} ${growOrNot()}`}
    >
      <div
        ref={leftContainerRef}
        style={{ flex: `0 0 ${leftWidth}%`, minWidth: "450px" }}
        className={cn(
          styles[`${cssClassName}__first-group`],
          growOrNot("__first-group"),
          {
            [styles[`${cssClassName}__first-group--less-margin`]]: isDraggable,
          },
        )}
      >
        {firstHalfChildren}
      </div>
      <div className={styles[`${cssClassName}__handle-container`]}>
        {isDraggable && (
          <div
            className={cn(styles[`${cssClassName}__handle-wrapper`], {
              [styles[`${cssClassName}__handle-wrapper--grabbed`]]: isDragging,
            })}
          >
            <div
              className={cn(styles[`${cssClassName}__handle`], {
                [styles[`${cssClassName}__handle--grabbed`]]: isDragging,
              })}
              onMouseDown={onMouseDown}
              style={{ cursor: isDragging ? "grabbing" : "grab" }}
              role="slider"
              tabIndex={0}
              onKeyDown={handleKeyDown}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={leftWidth}
              aria-orientation="horizontal"
            />
          </div>
        )}
      </div>
      <div
        ref={rightContainerRef}
        style={{ flex: `0 0 ${rightWidth}%`, minWidth: "450px" }}
        className={cn(
          styles[`${cssClassName}__second-group`],
          growOrNot("__second-group"),
          {
            [styles[`${cssClassName}__second-group--less-margin`]]: isDraggable,
          },
        )}
      >
        {secondHalfChildren}
      </div>
    </div>
  );
};
